import type { AnswersByKey } from "./types";

export interface RecommendedStory {
  slug: string;
  title: string;
  bibleBook: string;
  summary: string;
  coverUrl: string;
  videoMp4Url: string;
  videoWebmUrl: string;
}

const CDN_BASE = "https://assets.readparables.com";

const story = (
  slug: string,
  title: string,
  bibleBook: string,
  summary: string
): RecommendedStory => ({
  slug,
  title,
  bibleBook,
  summary,
  coverUrl: `${CDN_BASE}/stories/${slug}/cover.webp`,
  videoMp4Url: `${CDN_BASE}/funnel/story-loops/v3/${slug}.mp4`,
  videoWebmUrl: `${CDN_BASE}/funnel/story-loops/v1/${slug}.webm`,
});

const STORIES = {
  "the-flood": story(
    "the-flood",
    "The Flood",
    "Genesis",
    "As humanity spirals into total corruption, God resolves to wipe the earth clean with a catastrophic flood, but spares one man, Noah, tasking him with building a massive wooden ark to carry his family and every creature on earth through the deluge."
  ),
  goliath: story(
    "goliath",
    "Goliath",
    "1 Samuel",
    "For forty days, a nine-foot-nine Philistine warrior terrorized the entire Israelite army, until a shepherd boy named David walked onto the battlefield with a sling, five smooth stones, and no armor."
  ),
  "crossing-the-red-sea": story(
    "crossing-the-red-sea",
    "Crossing the Red Sea",
    "Exodus",
    "With Pharaoh's chariots thundering behind them and the Red Sea blocking every escape, two million Israelites stood trapped on the shore. Then Moses raised his staff, and a wall of water rose on either side."
  ),
  "joseph-s-rise-to-power": story(
    "joseph-s-rise-to-power",
    "Joseph's Rise to Power",
    "Genesis",
    "Falsely imprisoned in an Egyptian dungeon, a forgotten Hebrew slave named Joseph spends years in chains, until Pharaoh's haunting dreams pull him out of the pit and straight to the throne."
  ),
  "three-hundred-men": story(
    "three-hundred-men",
    "Three Hundred Men",
    "Judges",
    "Gideon faces a vast Midianite horde armed with just 300 men, each carrying only a torch, a clay jar, and a trumpet. When they shatter their jars in the darkness, the enemy army turns on itself in blind panic."
  ),
  "elijah-on-mount-carmel": story(
    "elijah-on-mount-carmel",
    "Elijah on Mount Carmel",
    "1 Kings",
    "Outnumbered 450 to 1, the prophet Elijah challenges the priests of Baal to a fire-lighting contest on Mount Carmel, their hours of screaming produce nothing but silence. When Elijah prays, fire falls from the sky."
  ),
  "the-birth-of-jesus": story(
    "the-birth-of-jesus",
    "The Birth of Jesus",
    "Luke",
    "A young woman named Mary delivers a baby in a stable, laying him in a feeding trough, while angels tear open the night sky to announce his birth to stunned shepherds in a nearby field."
  ),
  "jonah-and-the-fish": story(
    "jonah-and-the-fish",
    "Jonah and the Fish",
    "Jonah",
    "Jonah, a prophet ordered to deliver a message to a city he despised, boards a ship to the opposite end of the known world, only for a supernatural storm to expose his flight and swallow him into three days of darkness inside a massive fish."
  ),
  "call-of-moses": story(
    "call-of-moses",
    "Call of Moses",
    "Exodus",
    "An 80-year-old fugitive shepherd is minding his flock near Mount Sinai when a bush erupts in flames that never burn out, and the voice of God calls him by name, demanding he return to Egypt to confront the most powerful ruler on earth."
  ),
  "valley-of-dry-bones": story(
    "valley-of-dry-bones",
    "Valley of Dry Bones",
    "Ezekiel",
    "Exiled prophet Ezekiel stands alone in a vast valley carpeted with sun-bleached bones, the scattered remains of an entire nation. At God's command he speaks to the dead, and the bones rattle, reassemble, and rise as a living army."
  ),
  "two-camps": story(
    "two-camps",
    "Two Camps",
    "Genesis",
    "With four hundred men at Esau's back and a lifetime of betrayal between them, Jacob spends the night wrestling a mysterious stranger until the man wrenches his hip from its socket. At dawn he's renamed Israel, 'one who wrestles with God.'"
  ),
  creation: story(
    "creation",
    "Creation",
    "Genesis",
    "Before oceans had shores or stars had names, God spoke into a void of total darkness and, word by word, built a world, light, sky, land, creatures, and finally two people made in his own image."
  ),
  "water-to-wine": story(
    "water-to-wine",
    "Water to Wine",
    "John",
    "At a wedding in Cana, the wine runs dry mid-celebration. Jesus orders six stone jars filled to the brim with water, and what the servants draw out is wine so fine the host can't believe the groom saved the best for last."
  ),
  "daniel-s-decision": story(
    "daniel-s-decision",
    "Daniel's Decision",
    "Daniel",
    "Torn from his homeland and pressed into Babylon's royal court, a young captive named Daniel risks his life by refusing the king's food and wine, betting everything on a quiet ten-day test of vegetables and water."
  ),
  "wisdom-of-solomon": story(
    "wisdom-of-solomon",
    "Wisdom of Solomon",
    "1 Kings",
    "A young king confesses he feels like a lost child, so when God offers him anything he desires, Solomon asks not for wealth or victory, but for wisdom. His gift is tested immediately when two women fight over a living infant."
  ),
  "esther-becomes-queen": story(
    "esther-becomes-queen",
    "Esther Becomes Queen",
    "Esther",
    "Esther, a young Jewish woman hiding her identity inside the Persian royal palace, learns that a powerful official has convinced the king to exterminate her entire people, and the only person who can stop it is her."
  ),
  "naomi-and-ruth": story(
    "naomi-and-ruth",
    "Naomi and Ruth",
    "Ruth",
    "After losing her husband and both sons in a foreign land, a grieving widow named Naomi trudges back to Bethlehem in poverty, accompanied only by Ruth, her fiercely loyal daughter-in-law who refuses to abandon her."
  ),
  "balaam-s-donkey": story(
    "balaam-s-donkey",
    "Balaam's Donkey",
    "Numbers",
    "A prophet sets out to curse Israel for profit, but his donkey sees what he cannot, an angel blocking the road with a drawn sword. God uses the most unlikely voice to stop a man from walking into destruction."
  ),
  "esther-saves-her-people": story(
    "esther-saves-her-people",
    "Esther Saves Her People",
    "Esther",
    "Esther, a Jewish queen hiding her heritage in the Persian palace, must risk her life to expose a royal official's secret plot to exterminate her entire people. In a single banquet revelation, she names the villain to his face."
  ),
  "handwriting-on-the-wall": story(
    "handwriting-on-the-wall",
    "Handwriting on the Wall",
    "Daniel",
    "At a lavish feast for a thousand nobles, a disembodied hand appears and carves four cryptic words into the lamplit wall. The exiled prophet Daniel alone deciphers the verdict: the kingdom has been weighed and found wanting."
  ),
  "furnace-full-of-fire": story(
    "furnace-full-of-fire",
    "Furnace Full of Fire",
    "Daniel",
    "When Babylon's king erects a 90-foot gold statue and orders every official to bow or burn, three Jewish men refuse and are hurled into a furnace cranked seven times hotter than normal. They emerge untouched, alongside a mysterious fourth figure."
  ),
  "den-of-lions": story(
    "den-of-lions",
    "Den of Lions",
    "Daniel",
    "Daniel, the most trusted official in the Persian Empire, refuses to stop praying to his God even after jealous rivals trick the king into signing a law that sentences him to death by lion. He emerges at dawn without a scratch."
  ),
  "gideon-s-fleece": story(
    "gideon-s-fleece",
    "Gideon's Fleece",
    "Judges",
    "Gideon is a nobody hiding from a savage enemy army, threshing wheat in secret, when a messenger from God tells him he's been chosen to lead Israel's liberation. Too afraid to trust his own ears, he lays a wool fleece on the ground twice."
  ),
  "joseph-s-family-reunion": story(
    "joseph-s-family-reunion",
    "Joseph's Family Reunion",
    "Genesis",
    "Twenty years after selling their brother Joseph into slavery, ten desperate brothers arrive in Egypt begging grain, never suspecting the powerful governor staring them down is the boy they betrayed."
  ),
  "the-call-of-samuel": story(
    "the-call-of-samuel",
    "The Call of Samuel",
    "1 Samuel",
    "A young boy named Samuel, raised in God's temple, keeps waking in the night thinking the old priest is calling his name, until the priest realizes it's something far stranger. A voice in the dark answers back with a prophecy."
  ),
  "rebuilding-the-walls": story(
    "rebuilding-the-walls",
    "Rebuilding the Walls",
    "Nehemiah",
    "Nehemiah, a Jewish cupbearer to the Persian king, stakes everything on an audacious request to rebuild Jerusalem's ruined walls. Armed with royal letters and a crew of ordinary families, he outmaneuvers assassins in fifty-two relentless days."
  ),
} as const;

type StoryPair = [RecommendedStory, RecommendedStory];

// ─── Funnel A: Faith Reconnect (individual) ────────────────────────────────────
// Decision axes: topic_interest_grid (grouped into 3 themes) × quest_difficulty
//
// Interest themes derived from multi-select answers:
//   Epic/Stories: stories_characters, hard_times
//   Spiritual:   closer_to_god, jesus_teachings, peace_anxiety
//   Wisdom:      wisdom_guidance, purpose_direction, relationships

const FUNNEL_A_PAIRS: Record<string, StoryPair> = {
  // Epic + Explorer (simple dramatic stories)
  "epic:explorer": [STORIES["the-flood"], STORIES["goliath"]],
  // Epic + Adventurer (dramatic stories with context)
  "epic:adventurer": [STORIES["crossing-the-red-sea"], STORIES["three-hundred-men"]],
  // Epic + Scholar (complex epic narratives)
  "epic:scholar": [STORIES["joseph-s-rise-to-power"], STORIES["elijah-on-mount-carmel"]],
  // Spiritual + Explorer (accessible encounters with God)
  "spiritual:explorer": [STORIES["jonah-and-the-fish"], STORIES["the-birth-of-jesus"]],
  // Spiritual + Adventurer (personal God encounters with depth)
  "spiritual:adventurer": [STORIES["call-of-moses"], STORIES["the-call-of-samuel"]],
  // Spiritual + Scholar (deep spiritual themes)
  "spiritual:scholar": [STORIES["valley-of-dry-bones"], STORIES["two-camps"]],
  // Wisdom + Explorer (God's creative and generous power)
  "wisdom:explorer": [STORIES["creation"], STORIES["water-to-wine"]],
  // Wisdom + Adventurer (wisdom applied in real situations)
  "wisdom:adventurer": [STORIES["daniel-s-decision"], STORIES["wisdom-of-solomon"]],
  // Wisdom + Scholar (purposeful living with nuance)
  "wisdom:scholar": [STORIES["esther-becomes-queen"], STORIES["naomi-and-ruth"]],
  // Default
  default: [STORIES["crossing-the-red-sea"], STORIES["goliath"]],
};

// ─── Funnel B: Family Devotional ────────────────────────────────────────────────
// Decision axes: child_story_confidence (knowledge level) × quest_difficulty
//
// Knowledge levels:
//   Confident:    explain_well
//   Intermediate: gist_fuzzy
//   Beginner:     fumble, freeze

const FUNNEL_B_PAIRS: Record<string, StoryPair> = {
  // Confident + Explorer (fun, lesser-known stories)
  "confident:explorer": [STORIES["balaam-s-donkey"], STORIES["jonah-and-the-fish"]],
  // Confident + Adventurer (dramatic, surprising narratives)
  "confident:adventurer": [STORIES["elijah-on-mount-carmel"], STORIES["esther-saves-her-people"]],
  // Confident + Scholar (deep, prophetic stories)
  "confident:scholar": [STORIES["handwriting-on-the-wall"], STORIES["valley-of-dry-bones"]],
  // Intermediate + Explorer (safe, retellable to kids)
  "intermediate:explorer": [STORIES["the-flood"], STORIES["goliath"]],
  // Intermediate + Adventurer (vivid, memorable action)
  "intermediate:adventurer": [STORIES["crossing-the-red-sea"], STORIES["three-hundred-men"]],
  // Intermediate + Scholar (layered faith stories)
  "intermediate:scholar": [STORIES["joseph-s-rise-to-power"], STORIES["furnace-full-of-fire"]],
  // Beginner + Explorer (starting points everyone recognises)
  "beginner:explorer": [STORIES["creation"], STORIES["the-birth-of-jesus"]],
  // Beginner + Adventurer (accessible God encounters)
  "beginner:adventurer": [STORIES["call-of-moses"], STORIES["water-to-wine"]],
  // Beginner + Scholar (well-known with clear faith depth)
  "beginner:scholar": [STORIES["daniel-s-decision"], STORIES["den-of-lions"]],
  // Default
  default: [STORIES["the-flood"], STORIES["goliath"]],
};

// ─── Resolution helpers ─────────────────────────────────────────────────────────

/** Determine which funnel the user is in based on which answer keys exist. */
function isFamilyFunnel(answers: AnswersByKey): boolean {
  return "parent_status" in answers || "child_story_confidence" in answers;
}

/** Map topic_interest_grid multi-select answers to a dominant theme. */
function resolveInterestTheme(answers: AnswersByKey): "epic" | "spiritual" | "wisdom" {
  const raw = answers.topic_interest_grid;
  const selected: string[] = Array.isArray(raw) ? raw : typeof raw === "string" ? [raw] : [];

  const epicKeywords = ["stories_characters", "hard_times"];
  const spiritualKeywords = ["closer_to_god", "jesus_teachings", "peace_anxiety"];
  const wisdomKeywords = ["wisdom_guidance", "purpose_direction", "relationships"];

  let epic = 0;
  let spiritual = 0;
  let wisdom = 0;

  for (const id of selected) {
    if (epicKeywords.includes(id)) epic++;
    if (spiritualKeywords.includes(id)) spiritual++;
    if (wisdomKeywords.includes(id)) wisdom++;
  }

  if (epic >= spiritual && epic >= wisdom) return "epic";
  if (spiritual >= wisdom) return "spiritual";
  return "wisdom";
}

/** Map child_story_confidence to a knowledge tier. */
function resolveKnowledgeLevel(answers: AnswersByKey): "confident" | "intermediate" | "beginner" {
  const confidence = answers.child_story_confidence;
  if (confidence === "explain_well") return "confident";
  if (confidence === "gist_fuzzy") return "intermediate";
  return "beginner"; // fumble, freeze, or unset
}

// ─── Public API ─────────────────────────────────────────────────────────────────

export function getRecommendedStories(answers: AnswersByKey, funnelId?: "faith-reconnect" | "family-devotional"): StoryPair {
  const difficulty = typeof answers.quest_difficulty === "string" ? answers.quest_difficulty : "adventurer";

  if (funnelId === "family-devotional" || (!funnelId && isFamilyFunnel(answers))) {
    const knowledge = resolveKnowledgeLevel(answers);
    const key = `${knowledge}:${difficulty}`;
    return FUNNEL_B_PAIRS[key] ?? FUNNEL_B_PAIRS.default;
  }

  const theme = resolveInterestTheme(answers);
  const key = `${theme}:${difficulty}`;
  return FUNNEL_A_PAIRS[key] ?? FUNNEL_A_PAIRS.default;
}

export function preloadStoryCovers(answers: AnswersByKey): void {
  const stories = getRecommendedStories(answers);
  for (const story of stories) {
    const img = new Image();
    img.src = story.coverUrl;
  }
}

/** Every reachable story, deduplicated across both funnels. */
export function getAllRecommendedStories(): RecommendedStory[] {
  return [...new Map([...Object.values(FUNNEL_A_PAIRS), ...Object.values(FUNNEL_B_PAIRS)]
    .flat().map(story => [story.slug, story])).values()];
}

/** Avoid preloading a default pair before the selection questions are answered. */
export function hasStoryRecommendationAnswers(
  answers: AnswersByKey,
  funnelId?: "faith-reconnect" | "family-devotional",
): boolean {
  if (!["explorer", "adventurer", "scholar"].includes(String(answers.quest_difficulty))) return false;
  if (funnelId === "family-devotional" || (!funnelId && isFamilyFunnel(answers))) {
    return ["explain_well", "gist_fuzzy", "fumble", "freeze"].includes(String(answers.child_story_confidence));
  }
  const topics = Array.isArray(answers.topic_interest_grid) ? answers.topic_interest_grid : [answers.topic_interest_grid];
  return topics.some(topic => ["stories_characters", "hard_times", "closer_to_god", "jesus_teachings", "peace_anxiety", "wisdom_guidance", "purpose_direction", "relationships"].includes(String(topic)));
}
